import React, {useMemo} from 'react';
import {Provider} from 'react-redux';
import {createStore} from 'redux';
import {rootReducer} from '../../../redux/reducers';
import Header from '../../Header/Header';
import NewBuildingToolbarView from '../../TokensToolbar/NewBuildingToolbar/NewBuildingToolbarView';
import {getBuildingsList, useBuildingGroups} from '../../TokensToolbar/NewBuildingToolbar/NewBuildingToolbar';
import {SideMenuStackProvider} from '../../../contexts/SideMenuStackContext';
import {PlayersListSideMenuProvider} from '../../../contexts/PlayersListSideMenuContext';
import {PolicyMarketSideMenuProvider} from '../../../contexts/PolicyMarketSideMenuContext';
import {GamePanel, GAME_PANEL_MATERIALS, GAME_PANEL_SHAPES} from '../../gameUi/GamePanel';
import {GameButton, GAME_BUTTON_SIZES, GAME_BUTTON_VARIANTS} from '../../gameUi/GameButton';
import {EngravedIcon} from '../../gameUi/EngravedIcon';
import {GAME_UI_PHASES} from '../../../utils/gameUi/getGameUiPhase';
import {PHASES} from '../../../constants/phases';
import {GAME_TYPES} from '../../../constants/game';
import {PLAYER_COLORS} from '../../../configs/colors';
import {POLICY_CONFIGS} from '../../../configs/policies';
import {POLICIES} from '../../../constants/policies';
import {COMMON_BUILDING_TOKENS} from '../../../constants/tokens';
import {getKitCompleteButtonSamples, KitCompleteButton} from './KitCompleteButtons';

// Кто смотрит шапку: игрок в свой ход, игрок в чужой ход, зритель
export const KIT_HEADER_VIEWS = Object.freeze({
  OWN_TURN: 'own_turn',
  OTHER_TURN: 'other_turn',
  SPECTATOR: 'spectator',
});

const ME_ID = 'kit-me';
const SPECTATOR_ID = 'kit-spectator';
// Соперники с рейтинговыми образами всех трех лиг — у ходящего видно кольцо
// своего вида; ходит в «чужой ход» рубиновый
const RIVALS = [
  {id: 'kit-gold', name: 'Король Артур', factionId: 'humans', selectedSkin: 'humans_gold'},
  {id: 'kit-diamond', name: 'Вождь Гром', factionId: 'orcs', selectedSkin: 'orcs_diamond'},
  {id: 'kit-ruby', name: 'Тень Мэриэль', factionId: 'dark_elves', selectedSkin: 'dark_elves_ruby'},
];
const RIVAL_COLORS = [PLAYER_COLORS.RED, PLAYER_COLORS.BLUE, PLAYER_COLORS.DARK_GREEN, PLAYER_COLORS.PURPLE];

const KIT_PHASE_TO_PHASE = {
  [GAME_UI_PHASES.PREP]: PHASES.SETUP,
  [GAME_UI_PHASES.SUPPLY]: PHASES.SUPPLY,
  [GAME_UI_PHASES.CONSTRUCTION]: PHASES.CONSTRUCTION,
  [GAME_UI_PHASES.WAR]: PHASES.WAR,
  [GAME_UI_PHASES.END]: PHASES.WAR,
};

const OWN_POLICY = POLICY_CONFIGS.find((config) => config.id === POLICIES.AGRARIANISM);
// Купленная на следующий раунд — в шапке рядом с текущей, через стрелку
const OWN_NEXT_POLICY = POLICY_CONFIGS.find((config) => config.id === POLICIES.FORTIFICATIONISM);
// Предложения рынка — ряд миниатюр на кнопке рынка в шапке
const KIT_POLICY_MARKET = POLICY_CONFIGS
  .filter((config) => config.id !== OWN_POLICY.id && config.id !== OWN_NEXT_POLICY.id)
  .slice(0, 6);
const BASE_STATE = rootReducer(undefined, {type: '@@INIT'});

// Кнопка хода в углу сцены: в свой ход — с делами и таймером, иначе — ожидание
const getSceneCompleteButtonSample = (phase, warStage, headerView) => {
  const samples = getKitCompleteButtonSamples(phase, warStage);
  const preferredId = headerView === KIT_HEADER_VIEWS.OWN_TURN ? 'pending' : 'waiting';
  return samples.find((sample) => sample.id === preferredId) || samples[0];
};

/**
 * Пример состояния матча для настоящих компонентов шапки: четыре игрока,
 * свой — гном цвета, выбранного на витрине. В снабжении — одновременная фаза:
 * кольца бегут у всех, кто не закончил свою часть. В подготовке — у всех,
 * кто не подтвердил расстановку
 */
const buildKitState = ({phase, warStage, playerColor, headerView}) => {
  const rivalColors = RIVAL_COLORS.filter((color) => color !== playerColor);
  const me = {
    id: ME_ID,
    name: 'Гном Торин',
    color: playerColor,
    factionId: 'dwarves',
    coins: 12,
    coinsIncome: 5,
    winPoints: 7,
    winPointsIncome: 2,
    constructionPhaseActions: 3,
    policy: OWN_POLICY,
    nextPolicy: OWN_NEXT_POLICY,
  };
  const players = [
    me,
    ...RIVALS.map((rival, index) => ({
      ...rival,
      color: rivalColors[index],
      coins: 8 + index * 3,
      coinsIncome: 3 + index,
      winPoints: 5 + index * 2,
      winPointsIncome: 1,
      constructionPhaseActions: 3,
      policy: null,
      isBot: true,
    })),
  ];
  const turnPlayerId = headerView === KIT_HEADER_VIEWS.OWN_TURN ? ME_ID : 'kit-ruby';
  const gamePhase = KIT_PHASE_TO_PHASE[phase];

  return {
    ...BASE_STATE,
    shared: {
      ...BASE_STATE.shared,
      gameState: {
        ...BASE_STATE.shared.gameState,
        gameType: GAME_TYPES.ONLINE,
        isStarted: true,
        phase: gamePhase,
        warPhaseStage: warStage,
        round: 2,
        players,
        turnOrder: players.map((player) => player.id),
        currentPlayerIndex: players.findIndex((player) => player.id === turnPlayerId),
        isParallelSupply: gamePhase === PHASES.SUPPLY,
        phaseCompletedPlayers: gamePhase === PHASES.SUPPLY ? ['kit-diamond'] : [],
        playersConfirmedSetupPhase: gamePhase === PHASES.SETUP ? ['kit-gold'] : [],
        gameResults: phase === GAME_UI_PHASES.END ? players.map((player) => ({id: player.id})) : null,
        pendingBuildings: [],
        specialBuildings: {},
        policyMarket: KIT_POLICY_MARKET,
      },
    },
    private: {
      ...BASE_STATE.private,
      auth: {
        ...BASE_STATE.private.auth,
        id: headerView === KIT_HEADER_VIEWS.SPECTATOR ? SPECTATOR_ID : ME_ID,
        // Опытный игрок: описание строения по умолчанию скрыто — в футере все
        // строения сразу
        localGamesPlayed: 20,
        onlineGamesPlayed: 20,
      },
    },
  };
};

export const KitFooterPlate = ({title, children}) => (
  <GamePanel material={GAME_PANEL_MATERIALS.BAR} shape={GAME_PANEL_SHAPES.TOP} shadow
             className={'admin-game-ui-kit_compact-footer'}>
    <div className={'admin-game-ui-kit_footer-row'}>
      <span className={'admin-game-ui-kit_footer-title'}>{title}</span>
      <GameButton variant={GAME_BUTTON_VARIANTS.SUNK} size={GAME_BUTTON_SIZES.ICON} aria-label={'Справка'}>
        <EngravedIcon src={'/img/icons/info.png'} size={22}/>
      </GameButton>
      <GameButton variant={GAME_BUTTON_VARIANTS.SUNK} size={GAME_BUTTON_SIZES.ICON} aria-label={'Свернуть'}>
        <EngravedIcon src={'/img/icons/arrow down.png'} size={22}/>
      </GameButton>
    </div>
    {children}
  </GamePanel>
);

/**
 * Сцена матча на витрине: настоящая шапка (Header с тремя зонами: свое или
 * ходящий у зрителя, фаза с этапами войны, строка игроков с кольцами образов)
 * на примерном состоянии матча, компактный футер с тулбаром строительства
 * (все строения сразу), кнопка хода в правом нижнем углу (как на десктопе).
 * Футер в одну строку (чужой ход) — отдельным образцом на витрине
 */
const AdminGameUiKitMatchScene = ({phase, warStage, playerColor, headerView}) => {
  const state = useMemo(
    () => buildKitState({phase, warStage, playerColor, headerView}),
    [phase, warStage, playerColor, headerView]
  );
  const store = useMemo(() => createStore(rootReducer, state), [state]);
  const me = state.shared.gameState.players[0];
  const buildings = getBuildingsList(false, me, [], true);
  const buildingGroups = useBuildingGroups(buildings, true);

  return (
    // Новый стор на каждое изменение настроек — компоненты монтируются заново
    <Provider store={store} key={`${phase}-${warStage}-${playerColor}-${headerView}`}>
      <SideMenuStackProvider>
        <PlayersListSideMenuProvider>
          <PolicyMarketSideMenuProvider>
            <div className={'admin-game-ui-kit_match-scene'}>
              <div className={'admin-game-ui-kit_match-header'}>
                <Header handleOpenInGameMenu={() => {}} isSpectatorMode={headerView === KIT_HEADER_VIEWS.SPECTATOR}/>
              </div>

              <div className={'admin-game-ui-kit_match-footers'}>
                <KitFooterPlate title={'Размести строения'}>
                  <div className={'admin-game-ui-kit_footer-toolbar'}>
                    <NewBuildingToolbarView
                      buildings={buildings}
                      buildingGroups={buildingGroups}
                      selectedToken={COMMON_BUILDING_TOKENS.QUARTER}
                      currentRound={2}
                      availableCoins={me.coins}
                      onSelect={() => {}}
                    />
                  </div>
                </KitFooterPlate>
              </div>

              <div className={'admin-game-ui-kit_match-corner'}>
                <KitCompleteButton
                  sample={getSceneCompleteButtonSample(phase, warStage, headerView)}
                  me={me}
                  rival={state.shared.gameState.players[state.shared.gameState.currentPlayerIndex]}
                />
              </div>
            </div>
          </PolicyMarketSideMenuProvider>
        </PlayersListSideMenuProvider>
      </SideMenuStackProvider>
    </Provider>
  );
};

export default AdminGameUiKitMatchScene;
